import { useState } from "react";
import { ShieldCheck, Check, X, Eye } from "lucide-react";
import type { VerificationItem } from "../../data";
import { useAdminData } from "../../hooks/useAdminData";
import { verification as verificationApi } from "../../api/endpoints";
import AuthenticatedImage from "../../components/AuthenticatedImage";
import { AddToast } from "../../components/feedback/Toast";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { PageHeader } from "../../components/ui/PageHeader";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { checkStateLabel } from "../../theme/status";
import { CORAL, F, T } from "../../theme/tokens";
import { TableWrap } from "../../components/ui/TableWrap";

// ── Verification queue ───────────────────────────────────────────────────────
// The document previews here used to be four hardcoded Unsplash stock photos,
// shown for every single request regardless of what was actually submitted —
// which is a large part of why "Auto Verify appeared not to review/process
// the submitted verification data" (nothing on screen ever reflected the real
// submission). They're now the real files, fetched through
// verificationApi.mediaPath (identity documents are private; see
// AuthenticatedImage.tsx for why this can't be a plain <img src>).

export function VerificationView({ addToast }: { addToast: AddToast }) {
  const { VERIFICATION_QUEUE: queue, ADMIN_TALENTS, ADMIN_ORGS, refetchVerification, refetchTalents, refetchOrgs } = useAdminData();
  const [tab, setTab] = useState<"talents" | "organizations">("talents");
  const [drawerItem, setDrawerItem] = useState<VerificationItem | null>(null);

  const talentQueue = queue.filter((v) => v.entityType === "Talent");
  const orgQueue = queue.filter((v) => v.entityType === "Organisation");
  const activeQueue = tab === "talents" ? talentQueue : orgQueue;

  function approve(v: VerificationItem) {
    verificationApi.approve(v.id)
      .then(() => { refetchVerification(); refetchTalents(); refetchOrgs(); setDrawerItem(null); addToast(`${v.entityName} approved`, "success"); })
      .catch(() => addToast("Failed to approve", "error"));
  }
  function reject(v: VerificationItem) {
    verificationApi.reject(v.id, "Rejected by admin")
      .then(() => { refetchVerification(); refetchTalents(); refetchOrgs(); setDrawerItem(null); addToast(`${v.entityName} rejected`, "error"); })
      .catch(() => addToast("Failed to reject", "error"));
  }

  const TALENT_EMAILS: Record<string, string> = Object.fromEntries(ADMIN_TALENTS.map((t) => [t.id, t.email]));
  const ORG_EMAILS: Record<string, string> = Object.fromEntries(ADMIN_ORGS.map((o) => [o.id, o.email]));

  return (
    <div>
      <PageHeader title="Verification" sub={`${queue.length} pending verifications`} />

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: `1px solid ${T.border}`, marginBottom: "20px" }}>
        {([["talents", "Talents", talentQueue.length], ["organizations", "Organisations", orgQueue.length]] as const).map(([id, label, count]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding: "10px 20px", fontSize: "12px", fontWeight: tab === id ? 700 : 500, fontFamily: F, color: tab === id ? T.text : T.sub, background: "none", border: "none", borderBottom: tab === id ? `2px solid ${T.text}` : "2px solid transparent", cursor: "pointer", display: "flex", alignItems: "center", gap: "7px" }}>
            {label}
            {count > 0 && <span style={{ background: tab === id ? T.text : T.s2, color: tab === id ? "#fff" : T.sub, borderRadius: "9999px", fontSize: "10px", fontWeight: 700, padding: "1px 7px" }}>{count}</span>}
          </button>
        ))}
      </div>

      {/* Table */}
      <Card>
        <TableWrap compact hideOnPhone={["Email", "Auto-check", "Date Submitted"]}><table style={{ width: "100%", borderCollapse: "collapse", fontFamily: F }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              {tab === "talents"
                ? ["Email", "Name", "Category", "Auto-check", "Date Submitted", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: "10px", fontWeight: 700, color: T.muted, letterSpacing: "0.06em", textTransform: "uppercase" }}>{h}</th>
                ))
                : ["Email", "Organisation", "Auto-check", "Date Submitted", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: "10px", fontWeight: 700, color: T.muted, letterSpacing: "0.06em", textTransform: "uppercase" }}>{h}</th>
                ))
              }
            </tr>
          </thead>
          <tbody>
            {activeQueue.map((v) => {
              const email = tab === "talents" ? (TALENT_EMAILS[v.entityId] || "—") : (ORG_EMAILS[v.entityId] || "—");
              return (
                <tr key={v.id} style={{ borderBottom: `1px solid ${T.border}`, transition: "background 0.1s" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = T.s2; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ""; }}>
                  <td style={{ padding: "12px 14px", fontSize: "12px", color: T.sub, fontFamily: "'Space Mono', monospace" }}>{email}</td>
                  <td style={{ padding: "12px 14px", fontSize: "12px", fontWeight: 600, color: T.text }}>{v.entityName}</td>
                  {tab === "talents" && <td style={{ padding: "12px 14px", fontSize: "12px", color: T.sub }}>{v.category ?? "—"}</td>}
                  <td style={{ padding: "12px 14px" }}><StatusBadge status={checkStateLabel(v.checkState)} /></td>
                  <td style={{ padding: "12px 14px", fontSize: "11px", color: T.muted }}>{v.submittedDate}</td>
                  <td style={{ padding: "12px 14px" }}>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <Btn variant="ghost" size="xs" onClick={() => setDrawerItem(v)}><Eye size={11} /> View</Btn>
                      <Btn variant="primary" size="xs" onClick={() => approve(v)}><Check size={11} /> Approve</Btn>
                      <Btn variant="destructive" size="xs" onClick={() => reject(v)}><X size={11} /> Reject</Btn>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></TableWrap>
        {activeQueue.length === 0 && (
          <div style={{ padding: "48px", textAlign: "center", color: T.sub, fontSize: "13px", fontFamily: F }}>
            <ShieldCheck size={24} style={{ color: T.muted, marginBottom: "10px" }} />
            <p>No pending {tab === "talents" ? "talent" : "organisation"} verifications.</p>
          </div>
        )}
      </Card>

      {/* Detail side drawer */}
      {drawerItem && <div onClick={() => setDrawerItem(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 600 }} />}
      <div style={{
        position: "fixed", top: 0, right: 0, height: "100dvh", width: "min(460px, 100vw)",
        background: T.surface, borderLeft: `1px solid ${T.border}`,
        zIndex: 700, transform: drawerItem ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.25s cubic-bezier(0.4,0,0.2,1)",
        display: "flex", flexDirection: "column",
        boxShadow: drawerItem ? "-12px 0 40px rgba(0,0,0,0.1)" : "none",
        fontFamily: F,
      }}>
        {drawerItem && (
          <>
            {/* Drawer header */}
            <div style={{ padding: "18px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div>
                <p style={{ fontSize: "14px", fontWeight: 700, color: T.text, margin: 0 }}>{drawerItem.entityName}</p>
                <p style={{ fontSize: "11px", color: T.sub, margin: "2px 0 0" }}>
                  {drawerItem.entityType}{drawerItem.category ? ` · ${drawerItem.category}` : ""} · Submitted {drawerItem.submittedDate}
                </p>
              </div>
              <button onClick={() => setDrawerItem(null)} style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, padding: "4px" }}><X size={16} /></button>
            </div>

            {/* Drawer body */}
            <div style={{ flex: 1, overflowY: "auto", scrollbarWidth: "none", padding: "20px" }}>
              {/* Details */}
              <div style={{ marginBottom: "20px" }}>
                <p style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.1em", color: T.muted, textTransform: "uppercase", marginBottom: "10px" }}>Details</p>
                {[
                  ["Email", tab === "talents" ? (TALENT_EMAILS[drawerItem.entityId] || "—") : (ORG_EMAILS[drawerItem.entityId] || "—")],
                  tab === "talents" ? ["Name", drawerItem.entityName] : ["Organisation", drawerItem.entityName],
                  tab === "talents" ? ["Category", drawerItem.category ?? "—"] : ["CR number", drawerItem.category || "— none on file —"],
                  ["Date submitted", drawerItem.submittedDate],
                ].filter((x): x is [string, string] => x !== null).map(([label, value]) => (
                  <div key={label as string} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
                    <p style={{ fontSize: "11px", color: T.muted, fontFamily: F }}>{label as string}</p>
                    <p style={{ fontSize: "12px", fontWeight: 500, color: T.text, fontFamily: F }}>{value as string}</p>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
                  <p style={{ fontSize: "11px", color: T.muted, fontFamily: F }}>Auto-check</p>
                  <StatusBadge status={checkStateLabel(drawerItem.checkState)} />
                </div>
                {drawerItem.checkedAt && (
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
                    <p style={{ fontSize: "11px", color: T.muted, fontFamily: F }}>Checked at</p>
                    <p style={{ fontSize: "12px", fontWeight: 500, color: T.text, fontFamily: F }}>{drawerItem.checkedAt}</p>
                  </div>
                )}
                {drawerItem.checkNotes && (
                  <p style={{ fontSize: "11px", color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "8px", padding: "8px 10px", marginTop: "8px", fontFamily: F, lineHeight: 1.5 }}>
                    {drawerItem.checkNotes}
                  </p>
                )}
              </div>

              {/* Answers to the verification form (text, dates, choices) */}
              {drawerItem.details.length > 0 && (
                <div style={{ marginBottom: "20px" }}>
                  <p style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.1em", color: T.muted, textTransform: "uppercase", marginBottom: "10px" }}>Submitted answers</p>
                  {drawerItem.details.map((d) => (
                    <div key={d.label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
                      <p style={{ fontSize: "11px", color: T.muted, fontFamily: F }}>{d.label}</p>
                      <p style={{ fontSize: "12px", fontWeight: 500, color: T.text, fontFamily: F }}>{d.value}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Documents */}
              <p style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.1em", color: T.muted, textTransform: "uppercase", marginBottom: "12px" }}>
                {tab === "talents" ? "Verification Documents" : "Submitted Documents"}
              </p>

              {tab === "talents" || drawerItem.documents.length > 0 ? (
                drawerItem.documents.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    {drawerItem.documents.map((doc) => (
                      <div key={doc.url} style={{ border: `1px solid ${T.border}`, borderRadius: "10px", overflow: "hidden" }}>
                        <div style={{ padding: "8px 12px", background: T.s2, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: "6px" }}>
                          <span style={{ fontSize: "9px", fontWeight: 700, letterSpacing: "0.08em", color: T.muted, textTransform: "uppercase" }}>{doc.label}</span>
                        </div>
                        <AuthenticatedImage
                          src={verificationApi.mediaPath(doc.url)}
                          alt={doc.label}
                          style={{ width: "100%", height: "200px", objectFit: "cover", display: "block" }}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ fontSize: "12px", color: T.muted, fontFamily: F, padding: "16px 0" }}>No documents submitted yet.</p>
                )
              ) : (
                // Orgs submit no documents today — POST /api/org/verify takes no
                // body at all. The commercial registration number above (shown
                // in Details) is the only thing the automated check has to work
                // with; showing fabricated CR/VAT certificate images here (as
                // this drawer used to) implied a review that never happened.
                <div style={{ border: `1px dashed ${T.border}`, borderRadius: "10px", padding: "16px", background: T.s2 }}>
                  <p style={{ fontSize: "12px", color: T.sub, fontFamily: F, lineHeight: 1.6, margin: 0 }}>
                    Donatella does not yet collect KYB documents (CR certificate, VAT certificate, owner ID) for organisations.
                    Verification is based on the commercial registration number in Details above — confirm it independently
                    before approving.
                  </p>
                </div>
              )}
            </div>

            {/* Drawer footer */}
            <div style={{ padding: "16px 20px", borderTop: `1px solid ${T.border}`, display: "flex", gap: "8px", flexShrink: 0 }}>
              <button onClick={() => approve(drawerItem)} style={{ flex: 1, padding: "9px", borderRadius: "8px", fontSize: "12px", fontFamily: F, fontWeight: 700, cursor: "pointer", border: "none", background: CORAL, color: "#fff" }}>
                <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}><Check size={13} /> Approve</span>
              </button>
              <button onClick={() => reject(drawerItem)} style={{ flex: 1, padding: "9px", borderRadius: "8px", fontSize: "12px", fontFamily: F, fontWeight: 600, cursor: "pointer", border: `1px solid ${T.border}`, background: "#fee2e2", color: "#991b1b" }}>
                <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}><X size={13} /> Reject</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
